// series-7 (C.1, #490, 24-sep-2026): LA TARJETA PADRE DE LOS DOCUMENTOS PARTIDOS,
// DERIVADA DE LA LISTA QUE EL PANEL YA RECIBE.
//
// ARCHIVO EN ASCII A PROPOSITO (regla de la casa para los `.js` nuevos).
//
// `agruparPartes(docs)` recibe la lista ENTERA de GET /documents (el Panel la tiene
// completa: tambien trae las partes en Pendientes y las bloqueadas; es el Panel quien
// las aparta) y devuelve la lista ORDENADA de entradas de la lista del Panel:
//
//   {tipo: "doc", doc}     un documento que hoy sale en la lista: ni `pending`, ni
//                          `pending_credits`, ni `archived`, y que no sea una parte
//                          con marca de grupo;
//   {tipo: "padre", marca, nombre, total, ready, pendingCount, hijas, masReciente}
//                          las partes de un mismo troceo.
//
// SIN NINGUNA LLAMADA: todo sale de lo que ya viaja (sin brinco, F4.3b).
//
// LA LLAVE ES LA MARCA `parent_doc_id`, NUNCA EL NOMBRE: la lleva el 100 % de las
// partes (580 de 580 en produccion, censo del 24-sep, solo lectura), dos troceos con
// el mismo nombre dan dos padres, y una parte vieja sin marca se queda suelta, como
// hoy. `doc_group_hash` no sirve de llave (le falta a 25 partes de mayo y junio, y lo
// lleva tambien el documento "por confirmar" que todavia no es parte).
//
// EL NUMERO de cada parte es su indice guardado (`chunk_index`, base 1); si falta,
// el del nombre "Part n/N - ..." (o "Parte n/N - ...", el prefijo queda en el idioma
// de la subida). EL TOTAL es el N del troceo: `total_chunks` o, si falta (la foto de
// la lista de la app sin conexion no lo trae), el N del nombre. EL NOMBRE de la padre
// es lo que sigue a "Part n/N - ": el nombre original no se guarda en el documento
// (routes/documents.py:4342 lo deriva igual, partiendo por el PRIMER " - ").
//
// HIJAS = las partes del grupo que no estan en Pendientes: listas, parciales,
// procesandose (tambien en fila y "por confirmar"), con error y BLOQUEADAS (diseno
// 3.8: la parte archivada al bajar de plan va adentro, atenuada como hoy, aunque hoy
// un documento bloqueado no sale en la lista activa). `pending_credits` tampoco es
// hija: hoy no sale en la lista del Panel y no es una parte en Pendientes (cero en
// produccion). `ready` = listas + parciales. `pendingCount` = las del grupo en
// `pending`. Sin hijas no hay padre (todo en Pendientes: sigue en su franja); con una
// sola hija, si. Las hijas van por numero ascendente y nadie se renumera: si se
// borra la 4 quedan 3, 5 y 6.
//
// LA POSICION: la padre ocupa el lugar de su hija MAS RECIENTE (`created_at`), y el
// orden global es el de los documentos sueltos de hoy (lo mas nuevo arriba).
// DECLARADO: `lib/sortLibraryDocs.js` ancla el grupo en su parte MAS VIEJA; el diseno
// (3.2) pide la mas reciente, y esta funcion hace eso. `sortLibraryDocs` no se toca
// (lo usan el Centro de estudio, Administrar documentos, la bodega y el bajador).

// El nombre que el troceo escribe (routes/documents.py:2417 y :2684).
export const EXPRESION_DE_PARTE = /^(Part|Parte) (\d+)\/(\d+) - (.+)$/;

// Lo que hoy no sale en la lista activa del Panel (Dashboard.js:2568).
const FUERA_DE_LA_LISTA = new Set(['pending', 'pending_credits', 'archived']);
// Lo que no es hija de la padre: lo que esta en Pendientes.
const NO_ES_HIJA = new Set(['pending', 'pending_credits']);
// Lo que cuenta como "lista" en la linea de la padre.
const LISTAS = new Set(['ready', 'partial']);

/** "Part 3/6 - Contabilidad.pdf" -> {prefijo, numero, total, nombre}; si no casa, null. */
export function leerNombreDeParte(filename) {
  if (typeof filename !== 'string') return null;
  const m = EXPRESION_DE_PARTE.exec(filename);
  if (!m) return null;
  return { prefijo: m[1], numero: Number(m[2]), total: Number(m[3]), nombre: m[4] };
}

function entero(valor) {
  if (valor === undefined || valor === null || valor === '') return null;
  const n = Number(valor);
  return Number.isFinite(n) ? n : null;
}

/** El numero de la parte: su indice guardado o, si falta, el del nombre. */
export function numeroDeParte(doc) {
  const guardado = entero(doc && doc.chunk_index);
  if (guardado !== null) return guardado;
  const leido = leerNombreDeParte(doc && doc.filename);
  return leido ? leido.numero : null;
}

function totalDeParte(doc) {
  const guardado = entero(doc && doc.total_chunks);
  if (guardado !== null) return guardado;
  const leido = leerNombreDeParte(doc && doc.filename);
  return leido ? leido.total : null;
}

function nombreDeLaPadre(doc) {
  const filename = doc && typeof doc.filename === 'string' ? doc.filename : '';
  const leido = leerNombreDeParte(filename);
  if (leido) return leido.nombre;
  const corte = filename.indexOf(' - ');
  return corte >= 0 ? filename.slice(corte + 3) : filename;
}

const fechaDe = (doc) => (doc && doc.created_at) || '';

export function agruparPartes(docs) {
  const lista = (Array.isArray(docs) ? docs : []).filter((d) => d && typeof d === 'object');
  const grupos = new Map();
  const entradas = [];

  lista.forEach((doc, i) => {
    const marca = doc.parent_doc_id;
    if (marca) {
      if (!grupos.has(marca)) grupos.set(marca, []);
      grupos.get(marca).push({ doc, i });
      return;
    }
    if (!FUERA_DE_LA_LISTA.has(doc.status)) entradas.push({ entrada: { tipo: 'doc', doc }, fecha: fechaDe(doc), i });
  });

  grupos.forEach((partes, marca) => {
    const hijas = partes.filter(({ doc }) => !NO_ES_HIJA.has(doc.status));
    if (hijas.length === 0) return;

    // La mas reciente: la de `created_at` mayor; en empate, la primera que llego.
    let reciente = hijas[0];
    hijas.forEach((h) => { if (fechaDe(h.doc) > fechaDe(reciente.doc)) reciente = h; });

    const ordenadas = hijas
      .map((h) => ({ ...h, numero: numeroDeParte(h.doc) }))
      .sort((a, b) => {
        const na = a.numero === null ? Infinity : a.numero;
        const nb = b.numero === null ? Infinity : b.numero;
        if (na !== nb) return na - nb;
        return a.i - b.i;
      })
      .map((h) => h.doc);

    const totales = partes.map(({ doc }) => totalDeParte(doc)).filter((n) => n !== null);
    const numeros = partes.map(({ doc }) => numeroDeParte(doc)).filter((n) => n !== null);
    const total = totales.length > 0
      ? Math.max(...totales)
      : Math.max(partes.length, ...numeros);

    let nombre = '';
    for (const doc of ordenadas) {
      nombre = nombreDeLaPadre(doc);
      if (nombre) break;
    }

    entradas.push({
      entrada: {
        tipo: 'padre',
        marca,
        nombre,
        total,
        ready: ordenadas.filter((d) => LISTAS.has(d.status)).length,
        pendingCount: partes.filter(({ doc }) => doc.status === 'pending').length,
        hijas: ordenadas,
        masReciente: reciente.doc,
      },
      fecha: fechaDe(reciente.doc),
      i: reciente.i,
    });
  });

  // Lo mas nuevo arriba; en empate, el orden en que llego la lista.
  entradas.sort((a, b) => {
    if (a.fecha !== b.fecha) return a.fecha < b.fecha ? 1 : -1;
    return a.i - b.i;
  });
  return entradas.map((e) => e.entrada);
}
